import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import './ImageCarousel.css';

// variant: 'cover' (default) = images fill container with cropping
//          'blur' = images show fully with blurred background fill
const ImageCarousel = ({ images, alt, variant = 'cover' }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState([]);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
    const scrollTo = useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        setScrollSnaps(emblaApi.scrollSnapList());
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
    }, [emblaApi, onSelect]);

    if (!images || images.length === 0) {
        return (
            <div className="carousel-placeholder">
                <span>No Images</span>
            </div>
        );
    }

    return (
        <div className="embla">
            <div className="embla__viewport" ref={emblaRef}>
                <div className="embla__container">
                    {images.map((src, index) => (
                        <div className="embla__slide" key={index}>
                            {/* Blurred background image - only for 'blur' variant */}
                            {variant === 'blur' && (
                                <div
                                    className="embla__slide__bg"
                                    style={{ backgroundImage: `url(${src})` }}
                                />
                            )}
                            {/* Main image */}
                            <img
                                className={`embla__slide__img ${variant === 'blur' ? 'embla__slide__img--blur' : 'embla__slide__img--cover'}`}
                                src={src}
                                alt={`${alt} - ${index + 1}`}
                                loading="lazy"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {images.length > 1 && (
                <>
                    <button className="embla__prev" onClick={(e) => { e.stopPropagation(); scrollPrev(); }}>
                        <svg viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" /></svg>
                    </button>
                    <button className="embla__next" onClick={(e) => { e.stopPropagation(); scrollNext(); }}>
                        <svg viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
                    </button>
                    <div className="embla__dots">
                        {scrollSnaps.map((_, index) => (
                            <button
                                key={index}
                                className={`embla__dot ${index === selectedIndex ? 'is-selected' : ''}`}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); scrollTo(index); }}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default ImageCarousel;
